
import { defineComponent, PropType } from 'vue';
import { mapActions } from 'pinia';
import { type CartItem, type Item, WindcaveConfig } from '@white-label-types/parking-checkout';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { getAppPaymentMerchantId } from '@white-label-helper/get-app-payment-merchant-id';
import { getAppPaymentProviderConfig } from '@white-label-helper/get-app-payment-provider-config';
import { PAYMENT_PROVIDERS, PaymentConfig, FEATURE_FLAGS } from '@white-label-configuration/constants';
import { usePreloaderControllerStore } from '@white-label-store/preloader-controller';
import { addCustomMetric } from '@white-label-helper/shared-helpers';
import getAirport from '@white-label-helper/mixin-get-airport';
import { poolingBooking } from '@white-label-helper/api-parkings-payment-config';

export default defineComponent({
  mixins: [getAirport],
  props: {
    paymentsConfig: {
      type: Object as PropType<WindcaveConfig>,
      required: true,
    },
    totalValue: {
      type: Number,
      required: true,
    },
    isFormInvalid: {
      type: Boolean,
      required: true,
    },
    cartItems: {
      type: Array as PropType<CartItem[] | Item[]>,
      default: () => [] as CartItem[] | Item[],
    },
  },

  data: () => ({
    showError: false,
    currency: getAppVariable('default_currency'),
    windcaveConfig: {
      googlePayBusinessId: '',
      googlePayMerchantId: '',
      applePayMerchantId: '',
    },
    transactionStartTime: performance.now(),
  }),

  computed: {
    isApplePayEnabled(): boolean {
      return this.$launchDarkly.variation(FEATURE_FLAGS.IMP_1275_WINDCAVE_ENABLE_APPLE_PAY) && Boolean(this.windcaveConfig.applePayMerchantId);
    },
    isGooglePayEnabled(): boolean {
      return this.$launchDarkly.variation(FEATURE_FLAGS.IMP_1275_WINDCAVE_ENABLE_GOOGLE_PAY) && Boolean(this.windcaveConfig.googlePayMerchantId);
    },
    isMobileMenuEnabled(): boolean {
      return this.$launchDarkly.variation(FEATURE_FLAGS.IMP_1300_WINDCAVE_ENABLE_MOBILE_MENU);
    },
    isMobilePaymentEnabled(): boolean {
      return this.isGooglePayEnabled || this.isApplePayEnabled;
    },
    /**
     * Disables the payment widget if the form is invalid and mobile payment is enabled.
     * Exception: If the mobile menu feature is enabled, the payment widget remains active.
     */
    disablePaymentWidgetForm(): boolean {
      return this.isFormInvalid && this.isMobilePaymentEnabled && !this.isMobileMenuEnabled;
    },
    /**
     * Retrieves the brand color in RGBA format from the partner configuration.
     */
    brandColour(): string {
      return getAppVariable('colours.brand') || getAppVariable('brand_color', '');
    },

    product(): CartItem | {} {
      return this.cartItems?.length > 0 ? this.cartItems[0] : {};
    },

    /**
     * Computes the merchant name using the airport details from the get-app-airport mixin.
     * Formats it as "Airport Name (Airport Code)".
     */
    merchantName(): string {
      return `${this.airport.name} (${this.airport.code})`;
    },
  },

  mounted(): void {
    this.$global_eventEmitter.$on('windcaveHandleReload', this.handleWindcaveError);
    this.populateWindcaveConfig();
  },

  beforeDestroy (): void {
    this.$global_eventEmitter.$off('windcaveHandleReload', this.handleWindcaveError);
  },

  methods: {
    ...mapActions(usePreloaderControllerStore, {
      commitUpdatePreloader: 'updatePreloader',
    }),

    async handleWindcaveTransaction() {
      addCustomMetric("tracking", "Payment Metric: PreAuth", {
        partner_id: getAppVariable('partner_id'),
        provider: PAYMENT_PROVIDERS.WINDCAVE,
        partner_name: `${getAppVariable('partner_id')}-${PAYMENT_PROVIDERS.WINDCAVE}`
      }, this.transactionStartTime, 'payment_service');

      /**
       * We can time out waiting for wind cave,
       * so send a request to the back end to check status before moving on to
       * the next step in a payment process
       */
      poolingBooking(this.paymentsConfig.sessionId, 1000 * 5)
        .then(() => {
          // Only do this when we have an "Authorized" status
          this.$emit('payment-completed', {
            payment_method_id: this.paymentsConfig.sessionId,
            merchant_id: getAppPaymentMerchantId(),
          });
        })
        .catch((error) => {
          this.$emit('error');
          this.handleWindcaveError(true);
          addCustomMetric("error", "Payment Metric: PreAuth", {
            partner_id: getAppVariable('partner_id'),
            provider: PAYMENT_PROVIDERS.WINDCAVE,
            partner_name: `${getAppVariable('partner_id')}-${PAYMENT_PROVIDERS.WINDCAVE}`,
            error: error.message,
          },
            this.transactionStartTime,
            'payment_service'
          );
        });
    },

    /**
     * Handles the Windcave payment error event by showing the error message
     * and reinitializing the Windcave payment form, allowing the user to retry the payment.
     */
    handleWindcaveError(shouldShowError: boolean) {
      this.hideLoadingScreen();

      if (shouldShowError) {
        this.showError = true;
      }

      this.$emit('windcaveWidgetStartedLoading');
      this.$refs.payment?.createWindcavePaymentForm();
    },

    /**
     * Triggers the submit button inside 'windcave-ui' custom element.
     * This button is hidden with data-basic-styling property.
     */
    submitForm() {
      const btn: HTMLButtonElement | null = document.querySelector('#windcave-ui__submit');
      btn?.click();

      this.transactionStartTime = performance.now();
    },

    populateWindcaveConfig() {
      const activePaymentProvider: PaymentConfig | null = getAppPaymentProviderConfig();
      if (activePaymentProvider) {
        this.windcaveConfig.googlePayBusinessId = activePaymentProvider.config?.payment_google_business_id;
        this.windcaveConfig.googlePayMerchantId = activePaymentProvider.config?.payment_google_merchant_id;
        this.windcaveConfig.applePayMerchantId = activePaymentProvider.config?.payment_apple_merchant_id;
      }
    },

    /**
     * Hides the loading screen.
     * Triggered by the `windcaveTransactionCancelled` event from the payment widget
     * during early payment stages (e.g., when the user closes the Google Pay/Apple Pay modal,
     * 3DS authentication modal, etc.).
     */
    hideLoadingScreen() {
      this.commitUpdatePreloader('');
    },

    /**
     * Show the loading screen.
     * Triggered by the `windcaveTransactionStarted` event from the payment widget
     * when the customer starts payment process i.e.,
     * presses submit the card or start Apple Pay/Google Pay transaction.
     */
    showLoadingScreen() {
      this.commitUpdatePreloader(this.$t('UI.fullscreenPreloaderMessages.nearlyThere'));
    }
  },
})
